import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { kujoProducts } from "@/lib/kujoProducts";

import { ShieldCheck, Wind, Droplets, Feather, ArrowRight } from "lucide-react";

export default function KujoApparel() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Kujo Yardwear for Landscapers",
    description:
      "Premium footwear and apparel designed specifically for the green industry. Breathable, water-resistant, and built for the demands of landscaping.",
    brand: {
      "@type": "Brand",
      name: "Kujo Yardwear",
    },
  };

  return (
    <Layout>
      <SEO
        title="Kujo Yardwear | 911 Sourcing"
        description="Premium footwear and apparel designed specifically for the green industry. Breathable, water-resistant, and built for the demands of landscaping."
        url="/kujo-apparel"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative pt-24 pb-32 overflow-hidden bg-salt-dark">
        <div className="absolute inset-0 z-0">
          <img
            src="https://vibe.filesafe.space/1774883178162043817/assets/068f005c-6661-4843-9ddb-2877ee993786.jpg"
            alt="Landscaper working in Kujo Yardwear"
            className="w-full h-full object-cover opacity-30 grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-salt-dark via-salt-dark/80 to-transparent"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-block bg-plant-green text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-6">
                Official Partner
              </div>
              <h1 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tighter mb-6">
                Kujo Yardwear
              </h1>
              <p className="text-xl text-gray-300 font-medium mb-8 leading-relaxed max-w-2xl">
                The ultimate footwear and apparel built specifically for the
                green industry. Lightweight, breathable, and
                water-resistant—designed to keep your crews comfortable and
                productive all day.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://store.911sourcing.com/products-list/collections/kujo-yardwear"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-plant-green text-white px-8 py-4 rounded font-black uppercase tracking-widest hover:bg-plant-green/90 transition-colors text-center"
                >
                  Shop Kujo Yardwear
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Kujo */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tighter text-salt-dark mb-6">
              Built for the Yard
            </h2>
            <p className="text-lg text-gray-600 font-medium">
              Landscaping is demanding. Your gear should be up to the task. Kujo
              combines the durability of work wear with the comfort of athletic
              apparel.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-plant-green/10 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                <Wind className="w-8 h-8 text-plant-green" />
              </div>
              <h3 className="text-xl font-black uppercase tracking-tight text-salt-dark mb-4">
                Breathable
              </h3>
              <p className="text-gray-600 font-medium text-sm">
                Air mesh material allows heat to escape, keeping feet cool
                during hot summer days on the turf.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-plant-green/10 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                <Droplets className="w-8 h-8 text-plant-green" />
              </div>
              <h3 className="text-xl font-black uppercase tracking-tight text-salt-dark mb-4">
                Water-Resistant
              </h3>
              <p className="text-gray-600 font-medium text-sm">
                Tough SPU window provides a barrier against morning dew and
                light rain, keeping you dry.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-plant-green/10 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                <ShieldCheck className="w-8 h-8 text-plant-green" />
              </div>
              <h3 className="text-xl font-black uppercase tracking-tight text-salt-dark mb-4">
                Durable
              </h3>
              <p className="text-gray-600 font-medium text-sm">
                Reinforced construction and aggressive outsoles provide
                protection and grip on uneven terrain.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-plant-green/10 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                <Feather className="w-8 h-8 text-plant-green" />
              </div>
              <h3 className="text-xl font-black uppercase tracking-tight text-salt-dark mb-4">
                Lightweight
              </h3>
              <p className="text-gray-600 font-medium text-sm">
                Advanced materials offer superior comfort without the heavy
                fatigue of traditional work wear.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* All Products Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter text-center mb-4">
            The Full Collection
          </h2>
          <p className="text-center text-gray-500 font-medium mb-16 max-w-2xl mx-auto">
            Click any product to view full details, specifications, and shop.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {kujoProducts.map((product, i) => (
              <motion.div
                key={product.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.1 }}
              >
                <Link
                  to={`/kujo-apparel/${product.slug}`}
                  className="group flex flex-col h-full border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all hover:-translate-y-1 duration-300"
                >
                  <div className="h-64 bg-gray-50 relative flex items-center justify-center p-4 overflow-hidden border-b border-gray-100">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4 z-10">
                      <span className="bg-plant-green/90 text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm">
                        {product.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 bg-white flex-grow flex flex-col">
                    <h3 className="text-lg font-black uppercase tracking-tight text-salt-dark mb-2">
                      {product.name}
                    </h3>
                    <p className="text-sm text-plant-green font-bold uppercase tracking-wide mb-3">
                      {product.tagline}
                    </p>
                    <p className="text-sm text-gray-600 font-medium mb-6 flex-grow line-clamp-2">
                      {product.description}
                    </p>
                    <span className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-salt-dark group-hover:text-plant-green transition-colors">
                      View Details{" "}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-plant-green text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tighter mb-8">
            Outfit Your Crew
          </h2>
          <p className="text-xl text-white/90 font-medium mb-10 max-w-2xl mx-auto">
            Bulk pricing available for landscaping crews. Improve morale,
            safety, and productivity with gear built specifically for the job.
          </p>
          <a
            href="https://store.911sourcing.com/products-list/collections/kujo-yardwear"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-salt-dark text-white px-10 py-5 rounded font-black uppercase tracking-widest hover:bg-gray-900 transition-colors hover:scale-105 duration-300"
          >
            Shop Kujo Yardwear
          </a>
        </div>
      </section>
    </Layout>
  );
}
